// This is a mock API service that simulates backend interactions
// In a real application, this would make actual API calls to your backend

// Simulated delay for API calls
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Mock user data
const users = [
  { id: 1, username: 'demo', password: 'password', name: 'Demo User' }
];

// Mock conversation history
const conversations = [
  {
    id: 1,
    userId: 1,
    messages: [
      { id: 1, sender: 'user', text: 'What are the legal requirements for starting a business in the United States?' },
      { id: 2, sender: 'cola', text: 'Starting a business in the United States involves several legal requirements that vary by state and business type. Generally, you\'ll need to: 1) Choose a business structure (LLC, Corporation, etc.), 2) Register your business name, 3) Obtain an EIN (Employer Identification Number) from the IRS, 4) Register for state and local taxes, 5) Apply for necessary licenses and permits, and 6) Open a business bank account. Would you like me to elaborate on any specific aspect of these requirements?' }
    ]
  }
];

const API_BASE_URL = process.env.STRAPI_URL ?? process.env.REACT_APP_STRAPI_URL;
const CHAT_API_URL = process.env.CHAT_API_URL ?? process.env.REACT_APP_CHAT_API_URL;

// Mock API functions
export const api = {
  // Authentication
  login: async (email, password) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/local`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          identifier: email,
          password: password,
        }),
      });

      const data = await response.json();

      if (data.jwt) {
        localStorage.setItem('jwt', data.jwt); // Store JWT in localStorage
        return { success: true, jwt: data.jwt };
      } else if (data.error) {
        throw new Error(data.error.message || 'Login failed');
      } else {
        throw new Error('Unknown login error');
      }
    } catch (err) {
      throw err; // Propagate the error to the Login component
    }
  },
  
  // Get conversation history
  getConversations: async (userId) => {
    await delay(800);
    
    return conversations.filter(c => c.userId === userId);
  },
  
  // Send a message and get a response
  sendMessage: async (chatHistory, input, topic, country) => {
    try {
      const response = await fetch(`${CHAT_API_URL}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('jwt')}`,
        },
        body: JSON.stringify({
          chatHistory,
          input,
          topic,
          country
        }),
      });
      const data = await response.json();
      return data;
    } catch (err) {
      throw err;
    }
  },
  
  // Send feedback about a response
  sendFeedback: async (messageId, feedback) => {
    try {
      // Extract userId from the JWT token
      const token = localStorage.getItem('jwt');
      if (!token) {
        throw new Error('No JWT token found');
      }

      // Decode the JWT token to get the user ID
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));

      const userId = JSON.parse(jsonPayload).id;

      // Fetch the latest log entry for the current user
      const logsResponse = await fetch(`${API_BASE_URL}/api/user-logs?filters[User][$eq]=${userId}&sort=createdAt:desc&pagination[limit]=1`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const logsData = await logsResponse.json();
      
      if (logsData.data.length === 0) {
        throw new Error('No log entry found for the user');
      }

      const logId = logsData.data[0].id;

      // Update the latest log entry with the feedback
      const updateResponse = await fetch(`${API_BASE_URL}/api/user-logs/${logId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          data: {
            Liked: feedback
          }
        }),
      });
      const updateData = await updateResponse.json();
      return updateData;
    } catch (err) {
      throw err;
    }
  },
  
  // Get available countries
  getCountries: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/countries`);
      const data = await response.json();
      return data.data.map(country => ({
        id: country.id,
        name: country.attributes.Name
      }));
    } catch (err) {
      throw err;
    }
  },
  
  // Get available topics for a country
  getTopics: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/topics`);
      const data = await response.json();
      return data.data.map(topic => ({
        id: topic.id,
        name: topic.attributes.Name
      }));
    } catch (err) {
      throw err;
    }
  },

  changePassword: async (currentPassword, newPassword, confirmPassword) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('jwt')}`,
        },
        body: JSON.stringify({
          currentPassword: currentPassword,
          password: newPassword,
          passwordConfirmation: confirmPassword,
        }),
      });

      const data = await response.json();

      if (data.success || data?.user?.id) {
        return { success: true };
      } else if (data.error) {
        throw new Error(data.error.message || 'Password change failed');
      } else {
        throw new Error('Unknown error during password change');
      }
    } catch (err) {
      throw err;
    }
  },

  async forgotPassword(email) {
    return fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });
  },

  async resetPassword(resetToken, password, confirmPassword) {
    return fetch(`${API_BASE_URL}/api/auth/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        code: resetToken,
        password,
        passwordConfirmation: confirmPassword,
      }),
    });
  },
};

export default api;
